import { action, computed, makeObservable, observable } from 'mobx';
import {
    DevicesStore,
    IDeviceInfoProps as IDeviceInfoPropsDto,
} from '../devices-store';
import { notification } from 'ui-lib';
import { formatDate } from 'common-utils/date-utils';
import { DeviceAttributeMapping, DeviceAttributeName, ITaipanBatteryInfo as ITaipanBatteryInfoDto, IUpdateDeviceAttributeResponse, SCR_TAMPER_STATUS } from '../../dto/device-dto';
// eslint-disable-next-line no-duplicate-imports
import type { IScrTamperProps, IConnectionSummary, KeyDistributionResultDto } from '../../dto/device-dto';
import {
    getDisplayDeviceStatus,
    isDecommissionedDevice,
    isOnlineDevice,
    isNullOrEmpty,
    getDeviceLastOnline
} from '../../common-utils';
import { isEqual } from 'lodash';

type IDeviceInfoProps = IDeviceInfoPropsDto;
type ITaipanBatteryInfo = ITaipanBatteryInfoDto;

interface IDeviceInfosConstructorOptions {
    devicesStore: DevicesStore;
    uid: string;
    accountId: number;
}
export class DeviceInfosStore {
    protected readonly deviceStore: DevicesStore;
    protected readonly uid: string;
    protected readonly accountId: number;

    constructor(options: IDeviceInfosConstructorOptions) {
        makeObservable(this);
        this.deviceStore = options.devicesStore;
        this.uid = options.uid;
        this.accountId = options.accountId;
    }

    @observable
    loading: boolean;

    @action
    setLoading(loading: boolean) {
        this.loading = loading;
    }

    @observable
    error: string;

    @action
    setError(err: string) {
        this.error = err;
    }

    @observable
    groupLabel: string;

    @action
    setGroup(groupLabel: string) {
        this.groupLabel = groupLabel;
    }

    @observable
    keyDistributionResults: KeyDistributionResultDto[];

    @action
    setKeyInventory(keys: KeyDistributionResultDto[]) {
        this.keyDistributionResults = keys;
    }

    @computed
    get mspVersion(): string {
        return this.info?.attributes?.mspVersion;
    }

    @observable
    taipanBatteryInfo: ITaipanBatteryInfo;

    @action
    setTaipanBatteryInfo(taipanBatteryInfo: ITaipanBatteryInfo): void {
        this.taipanBatteryInfo = taipanBatteryInfo;
    }

    @computed
    public get displayedKeys(): string[] {
        if (isNullOrEmpty(this.keyDistributionResults)) return [];
        return this.keyDistributionResults.map((item) =>`${item.name}: ${item.status}`);
    }

    @computed
    private get scrInfo() {
        return this.info?.deviceInfo?.scrInfo || {};
    }

    @observable
    public status: string;

    @action
    public setConnectivityStatus(connectivity: IConnectionSummary): void {
        this.status = getDisplayDeviceStatus({ status: this.info?.status, connectivity: connectivity });
        this.lastOnline = getDeviceLastOnline(connectivity);
    }

    @observable
    public lastOnline: string;

    @computed
    public get lastUpdated(): string {
        return formatDate(this.info?.lastJobSuccessAt);
    }

    @observable
    timezone: string;

    @action
    setTimezone(timezone: string): void {
        this.timezone = timezone;
    }

    @computed
    public get serialNumber(): string {
        return this.info?.serialNumber;
    }

    @computed
    public get esn(): string {
        return this.info?.esn;
    }

    @computed
    public get name(): string {
        return this.info?.deviceLabel;
    }

    @computed
    public get location(): string {
        return this.info?.location;
    }

    @computed
    public get platform(): string {
        return this.info?.deviceTypeName;
    }
    @computed
    public get secureVersion(): string {
        return this.info?.firmwareVersion;
    }
    @computed
    public get trampolineVersion(): string {
        return this.info?.trampolineVersion;
    }
    @computed
    public get emvVersion(): string {
        return this.scrInfo['emvl1-sr']?.version;
    }

    @computed
    public get tamperInfo(): IScrTamperProps {
        return this.info?.tamper;
    }

    @computed
    public get managedState(): string {
        return isOnlineDevice(this.status) && this.tamperInfo?.status == SCR_TAMPER_STATUS.SCR_TAMPER ? 'UNMANAGED' : '';
    }

    @observable
    info: IDeviceInfoProps;

    @action
    setDeviceInfo(info: IDeviceInfoProps) {
        this.info = info;
    }

    @action
    public updateDeviceInfo(nextInfo: IDeviceInfoProps) {
        let hasChanged = false;
        const COMPARED_KEYS = ['deviceLabel', 'location', 'serialNumber', 'esn', 'firmwareVersion', 'tamper', 'attributes'];
        const updatedInfo = Object.assign({}, this.info);

       COMPARED_KEYS.forEach((key) => {
            if (!isEqual(this.info[key], nextInfo[key])) {
                updatedInfo[key] = nextInfo[key];
                hasChanged = true;
            }
        });

        if (!hasChanged) {
            return;
        }
        this.setDeviceInfo(updatedInfo);
    }
    @observable
    actionWaiting: boolean;

    @action
    setActionWaiting(updating: boolean) {
        this.actionWaiting = updating;
    }

    public hasValueChange(name: DeviceAttributeName, value: string): boolean {
        const prevValue = this.info[DeviceAttributeMapping[name]] || '';
        const currValue = value || '';

        return prevValue !== currValue;
    }

    public async editAttribute(name: DeviceAttributeName, value: string): Promise<IUpdateDeviceAttributeResponse> {
        this.setActionWaiting(true);
        try {
            const result = await this.deviceStore.updateDeviceAttribute(this.accountId, {
                deviceName: this.uid,
                attributeName: name,
                attributeValue: value,
            });

            if (result.updated) {
                const currentInfo = Object.assign({}, this.info);
                currentInfo[DeviceAttributeMapping[name]] = value;
                this.setDeviceInfo(currentInfo);
            }
            return result;
        } catch (e) {
            this.setError(e.message);
            return { updated: false };
        } finally {
            this.setActionWaiting(false);
        }
    }

    public updateConnectivityStatus(connectivity: IConnectionSummary): void {
        if (isDecommissionedDevice(this.status)) return;
        if (this.status == getDisplayDeviceStatus({ status: this.info.status, connectivity })) return;
        this.setConnectivityStatus(connectivity);
        notification['info']({
            message: `Device is ${this.status}`,
            placement: 'topRight'
        });
    }

    public async initializeData(device: IDeviceInfoProps): Promise<any> {
        this.setError(null);
        this.setLoading(true);
        try {
            this.setDeviceInfo(device);
            this.setGroup(device.deviceGroupLabel);
            
            await Promise.allSettled([
                (async () => {
                    const keys = await this.deviceStore.getKeyInventory(this.accountId, this.uid);
                    this.setKeyInventory(keys);
                })(),
                (async () => {
                    const taipanBatteryInfo = await this.deviceStore.getTaipanBatteryInfo(this.accountId, this.uid);
                    this.setTaipanBatteryInfo(taipanBatteryInfo);
                })(),
                (async () => {
                    const connectivity = await this.deviceStore.getDeviceConnectivity(this.accountId, this.uid);
                    this.setConnectivityStatus(connectivity);
                })(),
            ]);
            this.setTimezone(device.timezone);
        } catch (e) {
            this.setError(e.message);
        } finally {
            this.setLoading(false);
        }
    }
}
